import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { trackEvent, getAttribution } from "@/lib/tracking";
import { SectionTag, Reveal } from "@/components/Motion";

export const WA_NUMBER = "601164322150";

export const WA_MESSAGES = {
  default: "你好，我想免费了解 TikTok Shop 合作方式，请问可以介绍一下吗？",
  home: "你好，我想免费了解 TikTok Shop 合作方式，请问可以介绍一下吗？",
  dropshipping: "你好，我想了解一件代发 / 无需自行囤货的合作方式。",
  sideHustle: "你好，我想了解 TikTok Shop 电商副业合作，请问可以介绍一下吗？",
  merchant: "你好，我目前有产品 / 生意，想了解 TikTok Shop 合作。",
  landing: "你好，我想免费了解 TikTok Shop 合作方式，请问可以介绍一下吗？",
};

export function waLink(message = WA_MESSAGES.default) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
}

function pickMessage(meta) {
  if (meta.message) return meta.message;
  const p = window.location.pathname;
  if (p.startsWith("/lp/")) return WA_MESSAGES.landing;
  if (p === "/") return WA_MESSAGES.home;
  return WA_MESSAGES.default;
}

const _lastClickAt = {};

export function openWhatsApp(location, meta = {}) {
  const now = Date.now();
  trackEvent("whatsapp_click", {
    button_location: location,
    page_path: window.location.pathname,
    page_url: window.location.href,
    cta_text: meta.text,
    whatsapp_number: WA_NUMBER,
  });
  // Persist the click for the admin dashboard (fire-and-forget, never blocks
  // the WhatsApp open). Same button within 2s counts once (double-click guard).
  if (now - (_lastClickAt[location] || 0) > 2000) {
    _lastClickAt[location] = now;
    try {
      const attr = getAttribution();
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp-clicks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          page_path: window.location.pathname,
          page_url: window.location.href,
          button_location: location,
          cta_text: meta.text || null,
          gclid: attr.gclid || null,
          fbclid: attr.fbclid || null,
          utm_source: attr.utm_source || null,
          utm_medium: attr.utm_medium || null,
          utm_campaign: attr.utm_campaign || null,
        }),
      }).catch(() => {});
    } catch {
      /* telemetry must never break the CTA */
    }
  }
  window.open(waLink(pickMessage(meta)), "_blank", "noopener,noreferrer");
}

export function WhatsAppIcon({ size = 17, className = "" }) {
  return (
    <svg viewBox="0 0 448 512" width={size} height={size} className={className} fill="currentColor" aria-hidden="true">
      <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
    </svg>
  );
}

export function ChatButton({ children, location, variant = "primary", className = "", testId, message }) {
  const base =
    "group inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all duration-300";
  const styles = {
    primary: "bg-forest text-paper hover:bg-ink px-7 py-3.5 text-base",
    lime: "bg-lime text-ink hover:-translate-y-0.5 px-7 py-3.5 text-base shadow-[0_8px_30px_rgba(201,242,75,0.35)]",
    ghost: "border border-ink/20 text-ink hover:border-forest hover:text-forest px-7 py-3.5 text-base",
    sm: "border border-ink/15 text-ink hover:border-forest hover:text-forest px-4 py-2 text-sm",
  };
  return (
    <button
      onClick={() => openWhatsApp(location, { text: typeof children === "string" ? children : undefined, message })}
      className={`${base} ${styles[variant]} ${className}`}
      data-testid={testId || `chat-cta-${location}`}
      data-location={location}
    >
      <WhatsAppIcon size={variant === "sm" ? 14 : 17} className="transition-transform duration-300 group-hover:-rotate-12" />
      {children}
    </button>
  );
}

export function FinalCta() {
  return (
    <section className="relative overflow-hidden bg-forest py-16 text-paper md:py-24 grain" data-testid="final-cta">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-lime/15 blur-3xl" />
      <div className="mx-auto max-w-4xl px-5 text-center md:px-8">
        <Reveal>
          <h2 className="font-sans text-3xl font-bold leading-snug tracking-tight text-balance sm:text-4xl lg:text-5xl">
            不确定适不适合？<span className="whitespace-nowrap">直接 WhatsApp</span> 问我们
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-paper/70">
            无需填写表格，先了解再决定。点击按钮直接 WhatsApp 咨询 ——
            团队会先了解你的情况，再和你说明比较适合怎样开始。
          </p>
          <div className="mt-9">
            <ChatButton location="final-cta" variant="lime" testId="final-cta-chat-button">
              WhatsApp 咨询
            </ChatButton>
          </div>
          <p className="mt-5 text-xs text-paper/50">免费初步咨询</p>
        </Reveal>
      </div>
    </section>
  );
}

export function LearnMoreLink({ to, children }) {
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-1.5 text-sm font-semibold text-forest transition-colors hover:text-ink"
    >
      {children}
      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  );
}
